
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Eye, EyeOff } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { usePartnerProfile, useUpdatePartnerProfile, PartnerProfile } from "@/hooks/use-partner-profile";
import { PartnerSection } from "@/components/profile/PartnerSection";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { UsernameField } from "@/components/profile/UsernameField";
import { EmailNotificationPreferences } from "@/components/profile/EmailNotificationPreferences";
import { CameraAlertsCard } from "@/components/profile/CameraAlertsCard";
import { MfaSecurityCard } from "@/components/auth/MfaSecurityCard";

interface Profile {
  avatar_url: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  job_title?: string | null;
  country?: string | null;
  contact_type?: string | null;
  organization_id?: string | null;
  notes?: string | null;
  customer_since?: string | null;
  created_at?: string | null;
  user_status?: string | null;
  relationship_status?: string | null;
}

interface UserRole {
  role: string;
  organization_id: string;
}

interface Organization {
  id: string;
  name: string;
  industry?: string | null;
  size?: string | null;
  description?: string | null;
  website?: string | null;
  address?: string | null;
  country?: string | null;
}

const Profile = () => {
  const [profile, setProfile] = useState<Profile>({ 
    avatar_url: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    job_title: "",
    country: "",
    contact_type: "Buyer",
    organization_id: null,
    notes: "",
    customer_since: null,
    created_at: null
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [partnerData, setPartnerData] = useState<PartnerProfile | null>(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Partner hooks
  const { data: partnerProfile } = usePartnerProfile(currentUserId || undefined);
  const updatePartnerProfile = useUpdatePartnerProfile();

  useEffect(() => {
    getProfile();
  }, []);

  // Sync partner data with profile when partner profile loads
  useEffect(() => {
    if (partnerProfile) {
      setPartnerData(partnerProfile);
      // Pre-populate profile fields from partner data
      setProfile(prev => ({
        ...prev,
        first_name: prev.first_name || partnerProfile.first_name,
        last_name: prev.last_name || partnerProfile.last_name,
        email: prev.email || partnerProfile.email,
        phone: prev.phone || partnerProfile.phone,
      }));
    }
  }, [partnerProfile, userRole]);

  const getProfile = async () => {
    try {
      setError(null);
      console.log("🔍 Starting profile fetch...");
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log("👤 User data:", user);
      console.log("❌ User error:", userError);
      
      if (userError) {
        console.error("Error getting user:", userError);
        throw userError;
      }
      
      if (!user) {
        console.error("No user found - redirecting to login");
        setError("Please log in to view your profile");
        setLoading(false);
        return;
      }

      console.log("🔑 User authenticated:", user.id, user.email);
      setCurrentUserId(user.id);

      // Query using user_id instead of id
      console.log("📋 Querying profiles table...");
      const { data, error } = await supabase
        .from("profiles")
        .select("avatar_url, username, first_name, last_name, email, phone, job_title, country, contact_type, organization_id, notes, customer_since, created_at, user_status, relationship_status")
        .eq("user_id", user.id)
        .maybeSingle();

      console.log("📊 Profile query result:", { data, error });

      if (error) {
        console.error("Profile query error:", error);
        throw error;
      }
      
      if (data) {
        console.log("✅ Profile found, setting data...");
        const profileData = {
          avatar_url: data.avatar_url || "",
          username: data.username || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email || user.email || "",
          phone: data.phone || "",
          job_title: data.job_title || "",
          country: data.country || "",
          contact_type: data.contact_type || "Buyer",
          organization_id: data.organization_id,
          notes: data.notes || "",
          customer_since: data.customer_since,
          created_at: data.created_at,
          user_status: data.user_status,
          relationship_status: data.relationship_status
        };
        console.log("📝 Setting profile:", profileData);
        setProfile(profileData);

        // Fetch organization data if organization_id exists
        if (data.organization_id) {
          console.log("🏢 Fetching organization data for ID:", data.organization_id);
          const { data: orgData, error: orgError } = await supabase
            .from("organizations")
            .select("id, name, industry, size, description, website, address, country")
            .eq("id", data.organization_id)
            .maybeSingle();

          if (orgError) {
            console.error("Organization query error:", orgError);
          } else if (orgData) {
            console.log("✅ Organization found:", orgData);
            setOrganization(orgData);
          } else {
            console.log("⚠️ No organization found for ID:", data.organization_id);
          }
        }

        // Fetch user role information
        console.log("🔐 Fetching user role for user ID:", user.id);
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role, organization_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (roleError) {
          console.error("User role query error:", roleError);
        } else if (roleData) {
          console.log("✅ User role found:", roleData);
          setUserRole(roleData);
        } else {
          console.log("⚠️ No user role found for user:", user.id);
        }
      } else {
        console.log("⚠️ No profile found, creating default...");
        const defaultProfile = { 
          avatar_url: "", 
          first_name: "",
          last_name: "",
          email: user.email || "",
          phone: "",
          job_title: "",
          country: "",
          contact_type: "Buyer",
          organization_id: null,
          notes: "",
          customer_since: null,
          created_at: null
        };
        
        console.log("🆕 Creating new profile with data:", defaultProfile);
        
        const { error: insertError } = await supabase
          .from("profiles")
          .insert([{ 
            id: crypto.randomUUID(),
            user_id: user.id,
            ...defaultProfile
          }]);
          
        if (insertError) {
          console.error("❌ Profile insert error:", insertError);
          // Don't throw error, just use default profile
          console.log("⚠️ Using default profile despite insert error");
        } else {
          console.log("✅ Profile created successfully");
        }
        
        setProfile(defaultProfile);
      }
    } catch (error: any) {
      console.error("💥 Error in getProfile:", error);
      setError(error.message || "Error loading profile");
      toast.error("Error loading profile: " + (error.message || "Unknown error"));
    } finally {
      console.log("🏁 Profile loading complete");
      setLoading(false);
    }
  };

  const updateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      console.log("🔄 Starting profile update...");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      console.log("📝 Updating profile with data:", profile);

      // If user is a partner, update both profiles and partner tables
      if (partnerData) {
        updatePartnerProfile.mutate({
          partnerId: partnerData.id,
          profileData: {
            avatar_url: profile.avatar_url,
            first_name: profile.first_name,
            last_name: profile.last_name,
            email: profile.email,
            phone: profile.phone,
            job_title: profile.job_title,
            country: profile.country,
            contact_type: profile.contact_type,
            notes: profile.notes,
          },
          partnerData: {
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            email: profile.email || '',
            phone: profile.phone || '',
            partner_type: partnerData.partner_type,
            territory: partnerData.territory,
            specialization: partnerData.specialization,
          }
        });
      } else {
        // Regular profile update
        const { error } = await supabase
          .from("profiles")
          .update({
            avatar_url: profile.avatar_url,
            first_name: profile.first_name,
            last_name: profile.last_name,
            email: profile.email,
            phone: profile.phone,
            job_title: profile.job_title,
            country: profile.country,
            contact_type: profile.contact_type,
            notes: profile.notes,
          })
          .eq("user_id", user.id);

        if (error) {
          console.error("❌ Profile update error:", error);
          throw error;
        }
        console.log("✅ Profile updated successfully");
        toast.success("Profile updated successfully");
      }
    } catch (error: any) {
      console.error("💥 Error updating profile:", error);
      toast.error("Error updating profile: " + (error.message || "Unknown error"));
    }
  };

  const handlePartnerFieldChange = (field: keyof PartnerProfile, value: any) => {
    if (partnerData) {
      setPartnerData({ ...partnerData, [field]: value });
    }
  };

  const handleUsernameChange = async (newUsername: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("profiles")
        .update({ 
          username: newUsername,
          username_last_changed: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (error) throw error;

      setProfile({ ...profile, username: newUsername });
      toast.success("Username updated successfully");
    } catch (error: any) {
      console.error("Error updating username:", error);
      toast.error("Error updating username: " + (error.message || "Unknown error"));
      throw error;
    }
  };

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("You must select an image to upload.");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}-${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);

      if (updateError) throw updateError;

      setProfile({ ...profile, avatar_url: publicUrl });
      toast.success("Avatar updated successfully");
    } catch (error: any) {
      console.error("Error:", error);
      toast.error("Error uploading avatar");
    } finally {
      setUploading(false);
    }
  };

  // Password validation function (reused from EnhancedSignupForm)
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 10,
      hasLowercase: /[a-z]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      isPassphrase: pwd.length >= 16
    };

    const score = Object.values(requirements).filter(Boolean).length;
    const isStrong = requirements.minLength && 
      (requirements.isPassphrase || 
       (requirements.hasLowercase && requirements.hasUppercase && 
        requirements.hasNumber && requirements.hasSpecial));

    return { requirements, score, isStrong };
  };

  const updatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUpdatingPassword(true);

    try {
      // Validate new password
      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isStrong) {
        toast.error("New password does not meet security requirements");
        return;
      }

      if (newPassword !== confirmNewPassword) {
        toast.error("New passwords do not match");
        return;
      }

      // Verify current password by attempting to sign in
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast.error("User not found");
        return;
      }

      // Test current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      if (signInError) {
        toast.error("Current password is incorrect");
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Clear form fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      
      toast.success("Password updated successfully");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error("Error updating password: " + (error.message || "Unknown error"));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">Error: {error}</div>
          <Button onClick={() => window.location.reload()}>Reload Page</Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            {...PAGE_HEADERS.profile}
            description={profile.first_name && profile.last_name 
              ? `Manage settings for ${profile.first_name} ${profile.last_name}${profile.job_title ? ` (${profile.job_title})` : ''}`
              : PAGE_HEADERS.profile.description
            }
          />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Avatar</h2>
              <div className="flex items-center gap-4">
                {profile.avatar_url && (
                  <img
                    src={profile.avatar_url}
                    alt="Avatar"
                    className="rounded-full w-20 h-20 object-cover"
                  />
                )}
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={uploadAvatar}
                    disabled={uploading}
                    className="hidden"
                    id="avatar-upload"
                  />
                  <Button
                    onClick={() => document.getElementById("avatar-upload")?.click()}
                    disabled={uploading}
                    className="gap-2"
                    style={{ backgroundColor: "#DFEF97", color: "black" }}
                  >
                    <Upload className="h-4 w-4" />
                    {uploading ? "Uploading..." : "Upload Avatar"}
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Personal Information</h2>
              <form onSubmit={updateProfile} className="space-y-6">
                <div className="space-y-4">
                  <UsernameField
                    currentUsername={profile.username || ""}
                    userId={currentUserId || undefined}
                    onUsernameChange={handleUsernameChange}
                    isUpdating={false}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        type="text"
                        value={profile.first_name || ""}
                        onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        type="text"
                        value={profile.last_name || ""}
                        onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profile.email || ""}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-sm text-muted-foreground">Email cannot be changed here. Contact support if needed.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profile.phone || ""}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="Enter your phone number"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        type="text"
                        value={profile.country || ""}
                        onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                        placeholder="Enter your country"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="job_title">Job Title</Label>
                      <Input
                        id="job_title"
                        type="text"
                        value={profile.job_title || ""}
                        onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                        placeholder="Enter your job title"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="contact_type">Contact Type</Label>
                      <Select
                        value={profile.contact_type || "Buyer"}
                        onValueChange={(value) => setProfile({ ...profile, contact_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select contact type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Buyer">Buyer</SelectItem>
                          <SelectItem value="Decision Maker">Decision Maker</SelectItem>
                          <SelectItem value="Technical Contact">Technical Contact</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Input
                      id="notes"
                      type="text"
                      value={profile.notes || ""}
                      onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                      placeholder="Additional notes or bio"
                    />
                  </div>

                  {(profile.customer_since || profile.created_at) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      {profile.customer_since && (
                        <div className="space-y-2">
                          <Label>Customer Since</Label>
                          <Input
                            value={new Date(profile.customer_since).toLocaleDateString()}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                      
                      {profile.created_at && (
                        <div className="space-y-2">
                          <Label>Account Created</Label>
                          <Input
                            value={new Date(profile.created_at).toLocaleDateString()}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full text-black hover:opacity-80" style={{ backgroundColor: "#DFEF97" }}>
                  Update Profile
                </Button>
              </form>
            </Card>

            {/* Security Section */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Security</h2>
              <form onSubmit={updatePassword} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current-password"
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          disabled={isUpdatingPassword}
                          placeholder="Enter your current password"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={isUpdatingPassword}
                          aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
                        >
                          {showCurrentPassword ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                      <div className="relative">
                        <Input
                          id="confirm-new-password"
                          type={showConfirmNewPassword ? "text" : "password"}
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          required
                          disabled={isUpdatingPassword}
                          placeholder="Confirm your new password"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={isUpdatingPassword}
                          aria-label={showConfirmNewPassword ? "Hide confirm password" : "Show confirm password"}
                        >
                          {showConfirmNewPassword ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </div>
                      {confirmNewPassword && newPassword !== confirmNewPassword && (
                        <p className="text-xs text-red-600">New passwords do not match</p>
                      )}
                      {confirmNewPassword && newPassword === confirmNewPassword && newPassword && (
                        <p className="text-xs text-green-600">✓ New passwords match</p>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          disabled={isUpdatingPassword}
                          placeholder="Enter your new password"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={isUpdatingPassword}
                          aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                        >
                          {showNewPassword ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                      </div>
                      
                      {newPassword && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <div className="text-xs font-medium mb-2 text-muted-foreground">Password Requirements:</div>
                          <div className="space-y-1">
                            {(() => {
                              const validation = validatePassword(newPassword);
                              return (
                                <>
                                  <div className={`flex items-center text-xs ${validation.requirements.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    <span className="mr-2">{validation.requirements.minLength ? '✓' : '○'}</span>
                                    At least 10 characters
                                  </div>
                                  <div className={`flex items-center text-xs ${validation.requirements.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    <span className="mr-2">{validation.requirements.hasLowercase ? '✓' : '○'}</span>
                                    One lowercase letter (a-z)
                                  </div>
                                  <div className={`flex items-center text-xs ${validation.requirements.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    <span className="mr-2">{validation.requirements.hasUppercase ? '✓' : '○'}</span>
                                    One uppercase letter (A-Z)
                                  </div>
                                  <div className={`flex items-center text-xs ${validation.requirements.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    <span className="mr-2">{validation.requirements.hasNumber ? '✓' : '○'}</span>
                                    One number (0-9)
                                  </div>
                                  <div className={`flex items-center text-xs ${validation.requirements.hasSpecial ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    <span className="mr-2">{validation.requirements.hasSpecial ? '✓' : '○'}</span>
                                    One special character (!@#$%^&*)
                                  </div>
                                  {validation.requirements.isPassphrase && (
                                    <div className="flex items-center text-xs text-blue-600">
                                      <span className="mr-2">✓</span>
                                      Strong passphrase (16+ characters)
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          
                          <div className="mt-2">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                {(() => {
                                  const validation = validatePassword(newPassword);
                                  return (
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-300 ${
                                        validation.score <= 2 ? 'bg-red-500' : 
                                        validation.score <= 4 ? 'bg-yellow-500' : 
                                        'bg-green-500'
                                      }`}
                                      style={{ width: `${(validation.score / 6) * 100}%` }}
                                    />
                                  );
                                })()}
                              </div>
                              <span className={`text-xs font-medium ${(() => {
                                const validation = validatePassword(newPassword);
                                return validation.score <= 2 ? 'text-red-600' : 
                                       validation.score <= 4 ? 'text-yellow-600' : 
                                       'text-green-600';
                              })()}`}>
                                {(() => {
                                  const validation = validatePassword(newPassword);
                                  return validation.isStrong ? 'Strong' : 
                                         validation.score <= 2 ? 'Weak' : 'Fair';
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full text-black hover:opacity-80" 
                  style={{ backgroundColor: "#DFEF97" }}
                  disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword || !validatePassword(newPassword).isStrong}
                >
                  {isUpdatingPassword ? 'Updating Password...' : 'Update Password'}
                </Button>
              </form>
            </Card>

            <MfaSecurityCard />

            {/* Email Notification Preferences */}
            <EmailNotificationPreferences />
            <CameraAlertsCard />

            {/* Partner Information Section */}
            {partnerData && (
              <PartnerSection
                partnerData={partnerData}
                onPartnerFieldChange={handlePartnerFieldChange}
              />
            )}

            {organization && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Organization Information</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input
                        value={organization.name}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                    
                    {organization.industry && (
                      <div className="space-y-2">
                        <Label>Industry</Label>
                        <Input
                          value={organization.industry}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                    )}
                  </div>

                  {(organization.size || organization.website) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {organization.size && (
                        <div className="space-y-2">
                          <Label>Company Size</Label>
                          <Input
                            value={organization.size}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                      
                      {organization.website && (
                        <div className="space-y-2">
                          <Label>Website</Label>
                          <Input
                            value={organization.website}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {organization.description && (
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={organization.description}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                  )}

                  {(organization.address || organization.country) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {organization.address && (
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input
                            value={organization.address}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                      
                      {organization.country && (
                        <div className="space-y-2">
                          <Label>Organization Country</Label>
                          <Input
                            value={organization.country}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Profile;
